import { useState } from 'react';

import { useParams } from 'react-router-dom';

import { Box, CircularProgress } from '@mui/material';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { useUserPermissions } from 'hooks/useUserPermissions';

import UploadImage from 'components/UploadImage/UploadImage';

import useImages from '../../api/useImages';
import AcContentWrapper from '../../components/AcContentWrapper/AcContentWrapper';
import AcSearch from '../../components/AcSearch/AcSearch';
import AcViewWrapper from '../../components/AcViewWrapper/AcViewWrapper';
import FirstActionModal from '../../components/FirstActionModal/FirstActionModal';
import ImageItem from '../../components/ImageItem/ImageItem';
import PageTopBar from '../../components/Topbar/PageTopBar';
import { EToasterStatus, showToast } from '../../utils/showToast';

import { AssetsCategories } from './modals/constants/enums';
import DeleteAssetModal from './modals/DeleteAssetModal';

import './style.scss';
import '../view.scss';

interface Filter {
  name: string;
  type?: string;
}

export interface AssetsFormValues {
  file: File | null;
  imageId: string;
  category: string;
}

const AssetsView = () => {
  const { publisherId } = useParams();
  const { getImages } = useImages(publisherId);
  const { isActionEnabled } = useUserPermissions();
  const [filter, setFilter] = useState<Filter>({ name: '', type: 'All' });
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState<string>('');
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  if (getImages.isError) {
    showToast({
      message: 'Could not load assets list from server',
      status: EToasterStatus.ERROR
    });
  }

  const filterSearch = (value: string, category?: string) => {
    if (value !== filter.name || category !== filter.type) {
      setFilter({
        name: value,
        type: category
      });
    }
  };

  const openDeleteDialog = (imageId: string) => {
    setDeleteDialogOpen(true);
    setSelectedImageId(imageId);
  };

  const uploadAsset = () => {
    setIsImageModalOpen(true);
  };

  return (
    <AcViewWrapper
      header={
        <PageTopBar
          headline="Assets"
          buttons={[
            {
              text: 'Upload Asset',
              action: () => uploadAsset(),
              hidden: !isActionEnabled(
                null,
                EPermissionGroup.ASSET_LIBRARY,
                EPermissionAction.MANAGE
              )
            }
          ]}
        />
      }
    >
      <AcContentWrapper className="pb-20">
        <AcSearch
          categories={Object.keys(AssetsCategories).map(
            (key) => AssetsCategories[key as keyof typeof AssetsCategories]
          )}
          callback={filterSearch}
        />
        <Box className="gallery">
          {getImages.isLoading ? (
            <CircularProgress
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                height: '60px',
                width: '60px'
              }}
            />
          ) : getImages.data.length > 0 ? (
            getImages.data?.map((image: any, i: number) => {
              if (
                (filter.type ===
                  AssetsCategories[
                  image.type as keyof typeof AssetsCategories
                  ] ||
                  filter.type === 'All') &&
                (image.name.toLowerCase().includes(filter.name.toLowerCase()) ||
                  filter.name === '')
              )
                return (
                  <ImageItem
                    key={i}
                    deleteImage={openDeleteDialog}
                    data={image}
                    classes={'products'}
                    uploading={false}
                    type={image.type}
                  />
                );
              return null;
            })
          ) : (
            <FirstActionModal
              headline="Your asset library is still empty"
              text="Any visual you'll add here (art elements, background images etc.) will be available to use in your offers"
            />
          )}
        </Box>
        <DeleteAssetModal
          isModalOpen={isDeleteDialogOpen}
          onModalClosed={() => setDeleteDialogOpen(false)}
          setDeleteDialogOpen={setDeleteDialogOpen}
          selectedImageId={selectedImageId}
        />
        {isImageModalOpen && (
          <UploadImage
            title=""
            description=""
            buttonText=""
            selectedImageUrl={''}
            onUpload={() => {
              getImages.refetch();
            }}
            modalOnly
            uploadOnly
            isModalOpen={isImageModalOpen}
            externalModalOpenFunction={setIsImageModalOpen}
          />
        )}
      </AcContentWrapper>
    </AcViewWrapper>
  );
};

export default AssetsView;
